/**
 * Does this issues/pull-requests search query contain genuine free text?
 *
 * The repository Issues page defaults to a relevance ("Best match") sort, but relevance is only
 * meaningful when the user actually typed something to be relevant *to*. A query built entirely
 * from qualifiers, mentions and Boolean structure has no text to rank against, so it should keep
 * the historical newest-first default.
 *
 * For queries accepted by the backend parser, this mirrors `Search::Queries::FreeTextDetector` in
 * github/github. Text nodes and unsupported ordinary filter terms count as free text. We
 * deliberately do not reuse `@github-ui/filter-query`'s tokenizer here: its keyword matching is
 * case-insensitive, it does not strip grouping parentheses, and it classifies mentions as text, so
 * it disagrees with the grammar on exactly the inputs that matter. Keeping this helper
 * dependency-free also lets the service worker and other non-React consumers use it.
 *
 * For accepted queries, the two implementations gate the same decision on two tiers and form a
 * contract. Divergence reintroduces the bug where the requested sort and the displayed sort
 * disagree, so changes here need a matching change to `free_text_detector_test.rb`.
 */

/** Boolean operators in the issues grammar. Matching is exact and case-sensitive. */
const BOOLEAN_OPERATORS = new Set(['AND', 'OR'])

// No generated grammar data is available to this dependency-free package. Keep this aligned with
// `Search::Queries::ConditionalIssueQuery.field_list` in github/github.
const SUPPORTED_QUALIFIERS = new Set([
  'in',
  'sort',
  'author',
  'assignee',
  'team-review-requested',
  'mentions',
  'commenter',
  'involves',
  'milestone',
  'label',
  'lang',
  'language',
  'team',
  'user',
  'org',
  'owner',
  'repo',
  'created',
  'updated',
  'merged',
  'closed',
  'comments',
  'type',
  'state',
  'is',
  'has',
  'no',
  'status',
  'issue_type',
  'field',
  'head',
  'base',
  'reactions',
  'interactions',
  'project',
  'user-review-requested',
  'review',
  'reviewed-by',
  'review-requested',
  'archived',
  'draft',
  'linked',
  'reviewable-state',
  'reason',
  'parent-issue',
  'sub-issue',
  'team-review-requested-user',
  'review-involves',
  'suggestions',
  'milestone-number',
  'blocked-by',
  'blocking',
  'blocked',
  'relates-to',
])

/**
 * A user or `owner/name` mention, optionally negated: `@monalisa`, `-@monalisa`,
 * `@monalisa/github`.
 */
const MENTION_PATTERN = /^-?@[^\s/]+(?:\/[^\s/]+)?$/

const QUALIFIER_KEY_PATTERN = /^-?[A-Za-z0-9_+-]+(?:\.[A-Za-z0-9_+-]+)?$/
const FIELD_QUALIFIER_KEY_PATTERN =
  /^-?field\.[\p{L}\p{M}\p{N}\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\u200D\uFE0F_-]+$/u

const DOUBLE_QUOTE = '"'
const SINGLE_QUOTE = "'"
const OPEN_PAREN = '('
const CLOSE_PAREN = ')'

type QuoteDelimiter = typeof DOUBLE_QUOTE | typeof SINGLE_QUOTE

type Token = {
  /** The token with grouping parentheses removed. */
  readonly value: string
  /** Whether any part of the token was wrapped in quotes. */
  readonly hasQuotes: boolean
  /** Whether the token contains an empty single quoted segment. */
  readonly hasEmptySingleQuotes: boolean
  /** Index of the first colon that sits outside quotes, or -1. */
  readonly unquotedColonIndex: number
  /** Number of leading grouping parentheses outside quotes. */
  readonly leadingGroupingLength: number
  /** Number of trailing grouping parentheses outside quotes. */
  readonly trailingGroupingLength: number
}

function isQualifierKey(value: string): boolean {
  return QUALIFIER_KEY_PATTERN.test(value) || FIELD_QUALIFIER_KEY_PATTERN.test(value)
}

function canOpenSingleQuote(value: string, unquotedColonIndex: number, leadingGroupingLength: number): boolean {
  if (value.length === leadingGroupingLength) {
    return true
  }

  if (unquotedColonIndex <= leadingGroupingLength) {
    return false
  }

  return isQualifierKey(value.slice(leadingGroupingLength, unquotedColonIndex))
}

/**
 * Splits on whitespace, except inside supported quotes, so that `label:"needs triage"` and
 * `label:'needs triage'` each stay a single token. Records where quotes appeared because a colon
 * inside quotes is part of the text rather than a qualifier delimiter.
 *
 * This is a single linear pass with no backtracking.
 */
function tokenize(query: string): Token[] {
  const tokens: Token[] = []

  let value = ''
  let hasQuotes = false
  let hasEmptySingleQuotes = false
  let unquotedColonIndex = -1
  let quoteDelimiter: QuoteDelimiter | null = null
  let quoteHasContent = false
  let consecutiveBackslashes = 0
  let leadingGroupingLength = 0
  let trailingGroupingLength = 0
  let canCollectLeadingGrouping = true

  const pushToken = () => {
    if (value !== '' || hasQuotes) {
      tokens.push({
        value,
        hasQuotes,
        hasEmptySingleQuotes,
        unquotedColonIndex,
        leadingGroupingLength,
        trailingGroupingLength,
      })
    }
    value = ''
    hasQuotes = false
    hasEmptySingleQuotes = false
    unquotedColonIndex = -1
    leadingGroupingLength = 0
    trailingGroupingLength = 0
    canCollectLeadingGrouping = true
  }

  for (const character of query) {
    const isQuote = character === DOUBLE_QUOTE || character === SINGLE_QUOTE
    if (isQuote && quoteDelimiter === character) {
      if (consecutiveBackslashes % 2 === 1) {
        consecutiveBackslashes = 0
        value += character
        continue
      }

      if (character === SINGLE_QUOTE && !quoteHasContent) {
        hasEmptySingleQuotes = true
      }
      quoteDelimiter = null
      hasQuotes = true
      quoteHasContent = false
      consecutiveBackslashes = 0
      trailingGroupingLength = 0
      canCollectLeadingGrouping = false
      continue
    }
    const canOpenQuote =
      character === DOUBLE_QUOTE ||
      (character === SINGLE_QUOTE && canOpenSingleQuote(value, unquotedColonIndex, leadingGroupingLength))
    if (isQuote && quoteDelimiter === null && canOpenQuote) {
      quoteDelimiter = character
      hasQuotes = true
      quoteHasContent = false
      consecutiveBackslashes = 0
      trailingGroupingLength = 0
      canCollectLeadingGrouping = false
      continue
    }

    if (quoteDelimiter !== null) {
      quoteHasContent = true
      consecutiveBackslashes = character === '\\' ? consecutiveBackslashes + 1 : 0
    } else {
      consecutiveBackslashes = 0
    }

    if (quoteDelimiter === null && /\s/.test(character)) {
      pushToken()
      continue
    }

    if (character === ':' && quoteDelimiter === null && unquotedColonIndex === -1) {
      unquotedColonIndex = value.length
    }

    if (quoteDelimiter === null && character === OPEN_PAREN && canCollectLeadingGrouping) {
      leadingGroupingLength++
    } else {
      canCollectLeadingGrouping = false
    }

    trailingGroupingLength = quoteDelimiter === null && character === CLOSE_PAREN ? trailingGroupingLength + 1 : 0

    value += character
  }

  pushToken()

  return tokens
}

/**
 * Removes balanced grouping parentheses from the edges of a token and reports the net depth
 * change, so `(label:bug` and `label:docs)` can be classified on their qualifier alone while
 * still letting the caller detect an unbalanced query overall.
 */
function stripGrouping(token: Token): {value: string; depthChange: number; colonIndex: number} {
  const start = token.leadingGroupingLength
  const end = token.value.length - token.trailingGroupingLength
  const depthChange = token.leadingGroupingLength - token.trailingGroupingLength

  const colonIndex = token.unquotedColonIndex === -1 ? -1 : token.unquotedColonIndex - start

  return {
    value: token.value.slice(start, end),
    depthChange,
    colonIndex: colonIndex >= 0 && colonIndex < end - start ? colonIndex : -1,
  }
}

export function queryHasFreeText(query: string | null | undefined): boolean {
  if (!query || query.trim() === '') {
    return false
  }

  let depth = 0
  let sawFreeText = false

  for (const token of tokenize(query)) {
    const {value, depthChange, colonIndex} = stripGrouping(token)

    depth += depthChange

    // An unbalanced group means the real parser would raise, and the backend detector treats a
    // parse failure as "no free text" rather than guessing.
    if (depth < 0) {
      return false
    }

    // A token that was only parentheses, e.g. the `(` in `( label:bug OR label:docs )`.
    if (value === '' && !token.hasQuotes) {
      continue
    }

    if (token.hasEmptySingleQuotes) {
      sawFreeText = true
      continue
    }

    // Quotes always denote a quoted term. When the token also has a qualifier key the quotes
    // belong to the qualifier's value (`label:"needs triage"`), otherwise the quoted text is
    // the search term itself (`"app crashes"`).
    if (token.hasQuotes && colonIndex <= 0) {
      sawFreeText = true
      continue
    }

    if (BOOLEAN_OPERATORS.has(value)) {
      continue
    }

    if (MENTION_PATTERN.test(value)) {
      continue
    }

    if (colonIndex > 0) {
      const qualifier = value.slice(0, colonIndex)
      if (isQualifierKey(qualifier)) {
        const baseQualifier = qualifier.replace(/^-/, '').split('.', 1)[0]
        if (baseQualifier && SUPPORTED_QUALIFIERS.has(baseQualifier)) {
          continue
        }
      }
    }

    sawFreeText = true
  }

  if (depth !== 0) {
    return false
  }

  return sawFreeText
}

/**
 * Should a query default to the relevance ("Best match") sort?
 *
 * Mirrors the backend gate `best_match_enabled && (!require_free_text || free_text?(query))`.
 * An explicit `sort:` qualifier always wins and is resolved by the caller before this is asked.
 *
 * `requireFreeText` is the `issues_best_match_requires_free_text` feature flag. It is passed in
 * rather than read here so this package stays dependency-free and usable outside React.
 */
export function shouldDefaultToBestMatch(
  bestMatchEnabled: boolean | undefined,
  query: string | null | undefined,
  requireFreeText: boolean,
): boolean {
  if (!bestMatchEnabled) {
    return false
  }

  return !requireFreeText || queryHasFreeText(query)
}
